// src/components/employee-steps/StepReview.js
//
// Step 4 of the Add Employee wizard — read-only summary of Steps 1–3.
// This is where Save Employee fires.
//
// If the wizard is being run by a user without emp:pii:edit (Sensitive Info
// step was hidden), the Sensitive Info section here renders a "PII
// incomplete" notice instead of a summary, and the save proceeds with
// hasPii: false. PRD_UAM_CustomRoles2.md §6.5.

import React, { useEffect } from "react";
import Icon from "../icons/Icon";
import { toYMDString } from "../../utils/periodDate";
import ContributionBasisNote from "../ContributionBasisNote";

// 'YYYY-MM-DD' — the app-wide date standard (DESIGN-STANDARDS.md §13).
// toYMDString reads LOCAL Y/M/D components, never toISOString, so a Manila
// timestamp cannot render the previous day.
// Previously returned the raw string as-is (right format, by luck) and
// toISOString().split("T")[0] for a Timestamp — which is the banned pattern and
// could show the day before the one being saved.
const fmtDate = (value) => toYMDString(value) || "—";

const fmtMoney = (value) => {
  const num = parseFloat(value) || 0;
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(num);
};

const ReviewSection = ({ title, children }) => (
  <div
    style={{
      // --ui-surface, not --ui-bg: these sit ON the canvas, and a card the same
      // colour as the page behind it reads as a sunken well. Every other wizard
      // step uses white .form-section cards, so this matches them.
      background: "var(--ui-surface)",
      border: "1px solid var(--ui-border)",
      borderRadius: "8px",
      padding: "1rem",
      marginBottom: "1rem",
    }}
  >
    <div
      style={{
        fontSize: "0.8125rem",
        fontWeight: 700,
        color: "var(--ui-primary-hover)",
        marginBottom: "0.75rem",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const ReviewRow = ({ label, value, mono }) => (
  <div
    style={{
      display: "flex",
      gap: "12px",
      padding: "4px 0",
      fontSize: "0.8125rem",
    }}
  >
    <div style={{ color: "var(--ui-text-muted)", minWidth: "180px" }}>{label}</div>
    <div
      style={{
        color: "var(--ui-text)",
        fontWeight: 500,
        fontVariantNumeric: mono ? "tabular-nums" : "normal",
        fontSize: mono ? "0.75rem" : "0.8125rem",
      }}
    >
      {value || "—"}
    </div>
  </div>
);

export const StepReview = ({
  formData,
  hasSensitiveAccess,
  saving,
  handleSave,
  handlePrevious,
  // H11: the agencies this company composes from actual pay, e.g. ["SSS"].
  // Sourced by AddEmployee off the payrollRates snapshot it already reads —
  // this step has no useAuth and no read of its own. Defaults to empty, so a
  // failed or pending read renders no note rather than a wrong one.
  basisAgencies = [],
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const personal = formData.personalInfo || {};
  const employment = formData.employment || {};
  const banking = formData.banking || {};
  const govIds = formData.governmentIds || {};

  // When Sensitive Info step was skipped (no emp:pii:edit), the form data
  // for banking / governmentIds / salary is empty — the employee will be
  // created with hasPii: false. Surface this to the creator on Review.
  const willBeBasicOnly = !hasSensitiveAccess;

  // Working days summary line, e.g. "Mon, Tue, Wed, Thu, Fri"
  const workingDays = (() => {
    const order = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const schedule = employment.workSchedule || {};
    return order
      .map((day, idx) => (schedule[day]?.isWorkingDay ? labels[idx] : null))
      .filter(Boolean)
      .join(", ");
  })();

  return (
    <div className="add-employee-form-section">
      {/* PII incomplete notice — shown only when Sensitive Info step skipped */}
      {willBeBasicOnly && (
        <div
          style={{
            background: "var(--ui-warning-tint)",
            border: "1px solid var(--ui-warning)",
            borderRadius: "8px",
            padding: "1rem",
            marginBottom: "1.5rem",
            display: "flex",
            gap: "12px",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "var(--ui-warning)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontWeight: 700,
            }}
          >
            !
          </div>
          <div>
            <strong style={{ color: "var(--ui-warning-text)" }}>
              This employee will be created with payroll info incomplete
            </strong>
            <p style={{ color: "var(--ui-warning-text)", fontSize: "0.8125rem", marginTop: "4px" }}>
              Until a user with Sensitive Info access fills in salary, banking,
              and government IDs, this employee won't be eligible for payroll
              runs and will appear with a warning indicator on the All Employees
              list.
            </p>
            <p style={{ color: "var(--ui-warning-text)", fontSize: "0.8125rem", marginTop: "6px" }}>
              <strong>Continue?</strong>
            </p>
          </div>
        </div>
      )}

      <h2
        className="form-section-title"
        style={{ marginBottom: "1rem", color: "var(--ui-primary-hover)" }}
      >
        Review Employee Details
      </h2>

      <ReviewSection title="Personal Info">
        <ReviewRow
          label="Name"
          value={`${personal.firstName || ""} ${personal.middleName || ""} ${personal.lastName || ""}`.replace(/\s+/g, " ").trim()}
        />
        {personal.email && <ReviewRow label="Email" value={personal.email} />}
        {personal.mobile && <ReviewRow label="Mobile" value={personal.mobile} />}
        {personal.dateOfBirth && (
          <ReviewRow label="Date of Birth" value={personal.dateOfBirth} />
        )}
        {(personal.gender || personal.maritalStatus) && (
          <ReviewRow
            label="Gender / Marital"
            value={`${personal.gender || "—"} / ${personal.maritalStatus || "—"}`}
          />
        )}
      </ReviewSection>

      <ReviewSection title="Employment">
        <ReviewRow label="Department" value={employment.department || "—"} />
        <ReviewRow label="Start Date" value={fmtDate(employment.startDate)} />
        <ReviewRow
          label="Working Days"
          value={workingDays || "—"}
        />
        <ReviewRow
          label="Contributions"
          value={`SSS ${fmtMoney(employment.sssContribution)} · PhilHealth ${fmtMoney(employment.philhealthContribution)} · Pag-IBIG ${fmtMoney(employment.pagibigContribution)} · Tax ${fmtMoney(employment.withholdingTax)}`}
          mono
        />
        {/* H11: all four agencies share one row, so the note names whichever
            are composed rather than sitting under a single figure. One note,
            however many agencies: two stacked lines saying the same sentence
            would read as a rendering fault. */}
        {basisAgencies.length > 0 && (
          <ContributionBasisNote agencies={basisAgencies} />
        )}
      </ReviewSection>

      {hasSensitiveAccess ? (
        <ReviewSection title="Sensitive Info">
          <ReviewRow
            label="Salary"
            value={
              // A monthly employee stores 0 in dailySalary — their rate lives in
              // monthlySalary — so printing dailySalary unconditionally read
              // "monthly — ₱0.00/day" for every monthly hire. Show the field that
              // salaryType actually populates.
              employment.salaryType === "monthly"
                ? `monthly — ${fmtMoney(employment.monthlySalary)}/month`
                : `${employment.salaryType || "—"} — ${fmtMoney(employment.dailySalary)}/day`
            }
          />
          <ReviewRow
            label="Bank Account"
            value={banking.accountNumber}
            mono
          />
          <ReviewRow
            label="Account Name"
            value={banking.accountName}
          />
          <ReviewRow label="SSS" value={govIds.sss} mono />
          <ReviewRow label="PhilHealth" value={govIds.philhealth} mono />
          <ReviewRow label="Pag-IBIG" value={govIds.pagibig} mono />
          <ReviewRow label="TIN" value={govIds.tin} mono />
        </ReviewSection>
      ) : (
        <ReviewSection title="Sensitive Info — not collected">
          <p
            style={{
              color: "var(--ui-text-muted)",
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            User needs Sensitive Information access. Please contact your
            administrator.
          </p>
        </ReviewSection>
      )}

      {/* Form Actions */}
      <div className="add-employee-form-actions">
        <button
          onClick={handlePrevious}
          className="btn btn--secondary"
          disabled={saving}
        >
          <Icon name="arrow-left" size={14} aria-hidden="true" /> Previous
        </button>
        <button
          onClick={handleSave}
          className="btn btn--primary"
          disabled={saving}
        >
          {saving ? "Saving..." : willBeBasicOnly ? "Save (Confirm Incomplete)" : "Save Employee"}
        </button>
      </div>
    </div>
  );
};
